import { tokenExpiresAt } from '~/shared/lib/auth'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useEffectEvent,
} from 'react'
import { RestClient } from './zusRestClient'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Loader } from '~/shared/ui'
import { queueNotification } from '~/shared/notifications/actions'
import { getAccessToken } from './zusTokenExchange'
import { calculateNextRefreshDelay, shouldRefreshToken } from './zusTokenUtils'
import { logger } from '~/shared/lib/logger'

interface ZusSessionHandlerContext {
  zusApi: RestClient
  accessToken: string | undefined
}

// The provider below always supplies a real value; this empty default is a
// placeholder that is never read, so cast it to the context shape.
const ZusSessionContext = createContext<ZusSessionHandlerContext>({} as ZusSessionHandlerContext)

// Export hook for convenience
export const useZusSessionHandler = () => useContext(ZusSessionContext)

const ZusSessionProvider: React.FC<React.PropsWithChildren> = props => {
  const me = useSelector(state => state.me)
  const dispatch = useDispatch()
  const updateToken = () => getAccessToken(me.provider_fields?.tenant_key)
  const {
    data,
    error,
    isPending: isLoading,
    refetch,
  } = useQuery({
    queryKey: ['zusTokenData'],
    queryFn: () => updateToken(),
    staleTime: Infinity,
    retry: false, // Retries are handled manually
    select: data => ({
      accessToken: data.data.access_token,
      accessTokenExpiration: (Date.now() + data.data.expires_in * 1000).toString(),
    }),
  })

  const accessToken = data?.accessToken
  const getToken = useCallback(() => accessToken ?? '', [accessToken])

  const zusApi = useMemo(() => {
    const zusUrl = import.meta.env.VITE_ZUS_API_URL
    return new RestClient({
      url: zusUrl,
      getToken: () => Promise.resolve(getToken()),
      log: (message, params, isError) => {
        if (isError) logger.error('[Zus] API request failed', message, params)
      },
    })
  }, [getToken])

  // Refreshes the token if it is about to expire; resolves to the delay before the next check.
  const refreshTokenIfExpiring = useEffectEvent(async () => {
    const token = getToken()
    if (!token) return undefined
    const expiresAt = tokenExpiresAt(token)
    if (!shouldRefreshToken(expiresAt)) return calculateNextRefreshDelay(expiresAt)
    const result = await refetch()
    if (!result.data?.accessToken) throw new Error('Failed to refresh Zus token')
    return calculateNextRefreshDelay(tokenExpiresAt(result.data.accessToken))
  })

  // Keyed on the token so the checks start once it loads and restart from each refreshed one.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined
    let isCancelled = false
    const checkToken = async () => {
      try {
        const delay = await refreshTokenIfExpiring()
        if (delay === undefined || isCancelled) return
        timeoutId = setTimeout(() => void checkToken(), delay)
      } catch (e) {
        logger.error('[Zus] refresh access token', e)
      }
    }
    void checkToken()
    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
    }
  }, [accessToken])

  const renderChildren = () => {
    if (isLoading) return <Loader inline />
    else if (data?.accessToken) return props.children
    else if (!data?.accessToken || error) {
      dispatch(queueNotification({ variant: 'error', message: 'Failed to fetch Zus access token' }))
      return <></>
    }
  }

  return (
    <ZusSessionContext.Provider value={{ zusApi, accessToken: data?.accessToken }}>
      {renderChildren()}
    </ZusSessionContext.Provider>
  )
}

export default ZusSessionProvider
